import React, { useEffect, useState, useMemo } from 'react';
import { Loader2, RotateCcw, History, MessageSquareText, Trash2, CheckSquare, Square } from 'lucide-react';
import { requisitionsApi } from '../api';

const fmtDT = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d)) return '—';
  return d.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

export default function RollbackHistoryPanel({ theme }) {
  const [rows, setRows] = useState(null);
  const [err, setErr] = useState('');
  const [restoring, setRestoring] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(''); // '' | 'restore' | 'delete'

  const load = async () => {
    setErr('');
    try {
      const data = await requisitionsApi.listDeletedHistorical();
      setRows(data);
      // Drop selection ids that are no longer in the list
      setSelected(prev => new Set([...prev].filter(id => data.some(r => r.id === id))));
    } catch (e) {
      setErr(e.response?.data?.detail || 'Failed to load');
      setRows([]);
    }
  };
  useEffect(() => { load(); }, []);

  const allSelected = useMemo(() =>
    (rows?.length || 0) > 0 && rows.every(r => selected.has(r.id)),
    [rows, selected]);

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(rows.map(r => r.id)));
  };

  const restoreOne = async (id) => {
    if (!window.confirm(`Restore historical requisition ${id}? It will re-appear on the All Requisitions list and Excel exports.`)) return;
    setRestoring(id);
    try { await requisitionsApi.restoreDeleted(id); await load(); }
    catch (e) { alert(e.response?.data?.detail || 'Restore failed'); }
    finally { setRestoring(null); }
  };

  const bulkRestore = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`Restore ${ids.length} historical requisition${ids.length === 1 ? '' : 's'}? They will re-appear on the All Requisitions list.`)) return;
    setBulkBusy('restore');
    try {
      const r = await requisitionsApi.restoreBulk(ids);
      await load();
      alert(`Restored ${r.restored} of ${r.requested} selected`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Bulk restore failed');
    } finally { setBulkBusy(''); }
  };

  const bulkHardDelete = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    if (!window.confirm(`PERMANENTLY delete ${ids.length} historical requisition${ids.length === 1 ? '' : 's'}? This cannot be undone.`)) return;
    // Second confirm — this is destructive
    if (!window.confirm(`Really permanently delete? Type-check: OK to remove ${ids.length} row${ids.length === 1 ? '' : 's'}?`)) return;
    setBulkBusy('delete');
    try {
      const r = await requisitionsApi.hardDeleteBulk(ids);
      await load();
      alert(`Permanently deleted ${r.deleted} of ${r.requested} selected`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Bulk delete failed');
    } finally { setBulkBusy(''); }
  };

  if (rows === null) return <div className="p-6 text-center"><Loader2 className="animate-spin inline" size={16} /></div>;

  const selCount = selected.size;

  return (
    <div className="fm-card p-5" data-testid="rollback-history-panel">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <History size={16} style={{ color: theme.primary }} />
        <h3 className="text-[15px] font-semibold text-[#232323]">Deleted historical entries</h3>
        <span className="text-[12px] text-[#7a6f5c] ml-auto" data-testid="rollback-history-count">
          {rows.length} deleted historical requisition{rows.length === 1 ? '' : 's'}
        </span>
      </div>

      {rows.length > 0 && (
        <div className="flex items-center gap-2 mb-3 flex-wrap" data-testid="rollback-bulk-toolbar">
          <span className="text-[12px] text-[#7a6f5c]" data-testid="rollback-selected-count">
            {selCount} selected
          </span>
          <button
            data-testid="rollback-bulk-restore-btn"
            onClick={bulkRestore}
            disabled={selCount === 0 || bulkBusy !== ''}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA] disabled:opacity-40"
            style={{ color: theme.primary }}
          >
            {bulkBusy === 'restore' ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
            Restore selected
          </button>
          <button
            data-testid="rollback-bulk-delete-btn"
            onClick={bulkHardDelete}
            disabled={selCount === 0 || bulkBusy !== ''}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12.5px] font-medium border border-[#F2D2D2] bg-white hover:bg-[#FBEEEE] disabled:opacity-40"
            style={{ color: '#B84A4A' }}
            title="Permanently delete selected rows — cannot be undone"
          >
            {bulkBusy === 'delete' ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Delete permanently
          </button>
        </div>
      )}

      {err && <div className="text-[12px] text-[#B84A4A] mb-2" data-testid="rollback-history-error">{err}</div>}
      {rows.length === 0 ? (
        <div className="text-[13px] text-[#7a6f5c] py-3">
          No historical entries have been rolled back — nothing to review here.
        </div>
      ) : (
        <div className="border border-[#E7E2D3] rounded-md max-h-[420px] overflow-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[#F7F4EA] sticky top-0">
              <tr className="text-left text-[11.5px] text-[#7a6f5c]">
                <th className="px-3 py-2 font-medium w-[36px]">
                  <button
                    onClick={toggleAll}
                    data-testid="rollback-select-all"
                    className="inline-flex items-center"
                    title={allSelected ? 'Deselect all' : 'Select all'}
                  >
                    {allSelected ? <CheckSquare size={14} style={{ color: theme.primary }} /> : <Square size={14} />}
                  </button>
                </th>
                <th className="px-3 py-2 font-medium">Requisition</th>
                <th className="px-3 py-2 font-medium">Branch</th>
                <th className="px-3 py-2 font-medium">Original date</th>
                <th className="px-3 py-2 font-medium">Deleted by</th>
                <th className="px-3 py-2 font-medium">Deleted at</th>
                <th className="px-3 py-2 font-medium">Reason</th>
                <th className="px-3 py-2 font-medium text-right pr-4">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const on = selected.has(r.id);
                return (
                  <tr key={r.id}
                      className={`border-t border-[#EFEADC] ${on ? 'bg-[#FBF3DA]' : ''}`}
                      data-testid={`rollback-row-${r.id}`}>
                    <td className="px-3 py-2">
                      <button
                        onClick={() => toggle(r.id)}
                        data-testid={`rollback-checkbox-${r.id}`}
                        className="inline-flex items-center"
                      >
                        {on ? <CheckSquare size={14} style={{ color: theme.primary }} /> : <Square size={14} />}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono text-[12px]">{r.id}</td>
                    <td className="px-3 py-2">
                      <div>{r.ebo}</div>
                      <div className="text-[11px] text-[#7a6f5c] font-mono">{r.eboBranchCode}</div>
                    </td>
                    <td className="px-3 py-2 font-mono text-[12px]">{r.date}</td>
                    <td className="px-3 py-2">{r.deletedByName || r.deletedByEmail || '—'}</td>
                    <td className="px-3 py-2 text-[12px]">{fmtDT(r.deletedAt)}</td>
                    <td className="px-3 py-2 text-[12px] text-[#7a6f5c] max-w-[220px]" title={r.deletedReason}>{r.deletedReason || '—'}</td>
                    <td className="px-3 py-2 text-right pr-4">
                      <button
                        onClick={() => restoreOne(r.id)}
                        disabled={restoring === r.id || bulkBusy !== ''}
                        data-testid={`rollback-restore-${r.id}`}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-medium border border-[#E7E2D3] bg-white hover:bg-[#F7F4EA] disabled:opacity-50"
                        style={{ color: theme.primary }}
                      >
                        {restoring === r.id ? <Loader2 size={11} className="animate-spin" /> : <RotateCcw size={11} />}
                        Restore
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="mt-3 text-[11.5px] text-[#7a6f5c] flex items-center gap-1">
        <MessageSquareText size={11} /> Restore brings the entry back into every default query, Excel export, and duplicate-check so re-import doesn't create a phantom copy. Delete permanently removes the row from the database — cannot be undone.
      </p>
    </div>
  );
}
